import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, XCircle, Sparkles, KeyRound, ArrowRight, Loader2 } from "lucide-react";

// Public Accept / Decline page reached from the casting_cast email link.
// URL: /audition/response/:token   (?intent=decline lands straight on Decline).
// No auth — the token is the credential; losing it is losing the offer.
export default function AuditionResponse() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const wantsDecline = sp.get("intent") === "decline";
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(null);
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    api.get(`/auditions/response/${token}`)
      .then((r) => setMeta(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This link is no longer valid."))
      .finally(() => setLoading(false));
  }, [token]);

  const alreadyResponded = meta?.response_state === "accepted" || meta?.response_state === "declined";

  const accept = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/auditions/response/${token}/accept`);
      setAccepted(data);
      // Hand the user straight to the sign-in page with the temp password
      // filled in. The Login screen forces them onto /change-password once
      // the JWT lands, so they set a permanent password before hitting the
      // dashboard.
      if (data.temp_password && data.email) {
        setTimeout(() => {
          window.location.href = `/login?email=${encodeURIComponent(data.email)}&temp=${encodeURIComponent(data.temp_password)}`;
        }, 1500);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not accept — please try again.");
    } finally { setBusy(false); }
  };
  const decline = async () => {
    if (!confirm("Decline the role? The production team will be notified immediately.")) return;
    setBusy(true); setError("");
    try {
      await api.post(`/auditions/response/${token}/decline`);
      setDeclined(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not decline — please try again.");
    } finally { setBusy(false); }
  };

  const heading = useMemo(() => {
    if (accepted) return "You're in.";
    if (declined || meta?.response_state === "declined") return "Role declined.";
    if (meta?.response_state === "accepted") return "You've already accepted.";
    return `Hi ${meta?.name?.split(" ")[0] || "there"}.`;
  }, [accepted, declined, meta]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-lg" data-testid="audition-response-page">
        <div className="text-[10px] uppercase tracking-[0.28em] text-gold mb-3 inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> StageCall
        </div>

        {loading ? (
          <div className="text-zinc-500 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading your offer…</div>
        ) : error && !meta ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-6 text-sm text-red-200" data-testid="response-error">{error}</div>
        ) : (
          <>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight font-light" data-testid="response-heading">{heading}</h1>

            {/* Success — freshly accepted (temp password now emailed, not shown here) */}
            {accepted && (
              <div className="mt-6 rounded-lg border border-gold/30 bg-gold/5 p-5" data-testid="accepted-panel">
                <div className="text-[10px] uppercase tracking-widest text-gold">Welcome to {accepted.show_title}</div>
                <div className="mt-2 text-zinc-200">You've accepted the role of <b className="text-gold">{accepted.role}</b>.</div>
                <div className="mt-4 text-sm text-zinc-200">
                  We've emailed your sign-in details to <b className="text-white">{accepted.email}</b>.
                  {accepted.temp_password
                    ? " Taking you to sign in now…"
                    : " Use your existing StageCall password to sign in."}
                </div>
                <Link
                  to="/login"
                  data-testid="signin-link"
                  className="mt-5 inline-flex items-center gap-1.5 h-10 px-4 rounded-md font-semibold text-sm"
                  style={{ backgroundColor: "#F59E0B", color: "#0A0A0A" }}
                >
                  Sign in <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}

            {/* Success — freshly declined */}
            {declined && (
              <div className="mt-6 rounded-lg border border-white/10 bg-[#121212] p-5" data-testid="declined-panel">
                <div className="text-zinc-200">Thanks for letting us know. The production team has been notified and the role is open again.</div>
                <div className="mt-3 text-xs text-zinc-500">If this was a mistake, please reply directly to the offer email so the director can restore it.</div>
              </div>
            )}

            {/* Idle — show the offer summary + buttons */}
            {!accepted && !declined && meta && (
              <>
                <div className="mt-4 rounded-lg border border-white/10 bg-[#121212] p-5">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Role offer</div>
                  <div className="mt-2 text-2xl font-display text-gold" data-testid="offer-role">{meta.role}</div>
                  <div className="text-sm text-zinc-400">{meta.show_title}{meta.theatre ? ` · ${meta.theatre}` : ""}</div>
                  {meta.director && <div className="text-xs text-zinc-500 mt-2">Directed by {meta.director}</div>}
                </div>

                {alreadyResponded ? (
                  <div className="mt-5 text-sm text-zinc-400" data-testid="already-responded">
                    This offer was already {meta.response_state} on {meta.responded_at?.slice(0, 10)}.
                  </div>
                ) : (
                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <button
                      data-testid="accept-btn"
                      onClick={accept}
                      disabled={busy}
                      style={{ backgroundColor: "#F59E0B", color: "#0A0A0A" }}
                      className="h-11 px-5 rounded-md font-semibold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Accept the role
                    </button>
                    <button
                      data-testid="decline-btn"
                      onClick={decline}
                      disabled={busy}
                      className="h-11 px-5 rounded-md border border-white/15 text-zinc-200 hover:bg-white/5 text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" /> Decline
                    </button>
                  </div>
                )}
                {wantsDecline && !alreadyResponded && (
                  <div className="mt-3 text-[11px] text-zinc-500">You opened this from the “Decline” link — you can still change your mind above.</div>
                )}
                {error && <div className="mt-4 text-sm text-red-300" data-testid="response-error">{error}</div>}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
